/**
 * 「이 초안을 저장하면 **어떤 행이 남는가**」.
 *
 * 저장소가 아니라 여기 있는 이유 — 이 규칙은 **어디에 저장하든 같다.** 인메모리와
 * Supabase 가 각자 들고 있으면 한쪽만 고쳤을 때 화면이 조용히 갈린다. 테스트는
 * 인메모리만 돌므로 그 어긋남을 아무도 못 잡는다.
 *
 * 여기서 정하는 것은 다섯 가지다.
 *
 * | | |
 * |---|---|
 * | 빈 줄 털기 | 아무것도 안 적힌 공종·항목은 굳히지 않는다 |
 * | 공정 물려받기 | 이 견적에서 고쳐 둔 공정(도배를 먼저)은 다시 저장해도 남는다 |
 * | 공정표 정리 | 시공 순서로 줄 세우기 · 같은 공정 두 번 막기 · 공종 없는 공정 빼기 |
 * | 단가표 추출 | 발송할 때 단가가 있는 **말단** 항목만 뽑는다 |
 * | 템플릿 펼치기 | 템플릿 공종 → 빈 견적 뼈대 |
 *
 * **금액은 여기서 안 센다.** `calcQuote` 가 센 값을 자리에 박을 뿐이다.
 */

import { areaPairOf } from "./area-unit";
import { calcQuote, CalcItemResult } from "./calc";
import { DEFAULT_VAT_MODE } from "../money/vat";
import { convertUnitPrice } from "./margin";
import { QuoteDraftInput, QuoteItemInput, QuoteTradeInput } from "./schema";
import { summarizeSchedule } from "./schedule";
import {
  Quote,
  QuoteItem,
  QuotePhasePlan,
  QuoteTrade,
} from "./types";
import { PriceUpsertInput, TradeTemplate } from "../template/types";
import { Phase } from "../template/phases";
import { Trade } from "../template/trades";
import { visibleRows } from "../template/visible";
import { QuoteDetail } from "./types";

/**
 * 아무것도 안 적힌 줄인가. 이름·구분·비고가 모두 비었고 수량·단가가 0이며
 * 자식도 없으면 빈 줄이다.
 */
export const isBlankItem = (item: QuoteItemInput): boolean =>
  item.item.trim() === "" &&
  item.scope.trim() === "" &&
  item.unit.trim() === "" &&
  (item.space_code ?? "") === "" &&
  item.note.trim() === "" &&
  item.qty === 0 &&
  item.material_unit_price === 0 &&
  item.labor_unit_price === 0 &&
  (item.cost_unit_price ?? null) === null &&
  item.children.length === 0;

/**
 * 저장할 가치가 있는 공종인가.
 *
 * 새 견적은 템플릿의 공종을 전부 안고 시작한다. 그중 안 쓴 것까지 굳히면
 * **견적서에 빈 칸만 있는 공종이 줄줄이 찍히고**, 다음에 열었을 때 화면도 그만큼
 * 길어진다. 조건문을 적어 둔 공종은 값이 없어도 뜻이 있으므로 남긴다.
 */
export const isBlankTrade = (
  trade: QuoteDraftInput["trades"][number]
): boolean =>
  trade.condition_text.trim() === "" && trade.items.every(isBlankItem);

/** 저장하며 털어 낸 공종 이름. 화면이 「값이 없어 빼 두었습니다」 로 알린다. */
export const droppedTradeNames = (draft: QuoteDraftInput): string[] =>
  draft.trades
    .filter(isBlankTrade)
    .map((t) => t.name.trim())
    .filter((name) => name !== "");

/** 템플릿 공종을 초안 입력으로. 항목은 이름만 있고 수량·단가는 비어 있다. */
export const draftFromTemplate = (
  template: TradeTemplate,
  trades: readonly Trade[]
): QuoteTradeInput[] => {
  const byCode = new Map(trades.map((t) => [t.code, t]));

  return template.trades
    .slice()
    .sort((a, b) => a.sort_order - b.sort_order)
    .flatMap((t) => {
      const def = byCode.get(t.trade);
      // 목록에 없거나 숨긴 공종은 뼈대에서 뺀다.
      if (!def || !def.active) return [];
      return [
        {
          group: def.group,
          trade: t.trade,
          name: def.name,
          sort_order: def.sort_order,
          condition_text: "",
          items: [],
        },
      ];
    });
};

/**
 * 발송한 견적의 잎 항목을 단가표로 올린다. SET 부모는 단가가 없으니 건너뛴다.
 * 가견적은 항목이 없어 자연히 빈 배열이 된다 — 평당단가를 항목 단가표에 섞으면
 * 자동완성이 엉뚱한 값을 제안한다.
 */
export const pricesFromDetail = (detail: QuoteDetail): PriceUpsertInput[] => {
  // 단가표는 공급가(부가세 별도) 기준 하나 — 포함 견적이면 단가를 round(÷1.1) 해서 올린다.
  const mode = detail.quote.vat_mode ?? DEFAULT_VAT_MODE;
  const toSupply = (price: number): number => convertUnitPrice(price, mode, "EXCL");
  const parents = new Set(
    detail.items.map((i) => i.parent_id).filter((id): id is string => !!id)
  );
  const tradeById = new Map(detail.trades.map((t) => [t.id, t]));

  return detail.items
    .filter((i) => !parents.has(i.id))
    .filter((i) => i.item.trim().length > 0)
    .filter((i) => i.material_unit_price > 0 || i.labor_unit_price > 0)
    .map((i) => {
      const trade = tradeById.get(i.quote_trade_id);
      if (!trade) throw new Error(`항목 ${i.id} 의 공종을 찾을 수 없습니다`);
      return {
        trade: trade.trade,
        scope: i.scope,
        item: i.item,
        unit: i.unit,
        material_unit_price: toSupply(i.material_unit_price),
        labor_unit_price: toSupply(i.labor_unit_price),
      };
    });
};

export interface BuildRowsInput {
  quote: Quote;
  draft: QuoteDraftInput;
  /** 표준 + 회사 행을 **합치기 전** 그대로. 여기서 합친다. */
  trades: readonly Trade[];
  phases: readonly Phase[];
  /**
   * 지금 저장돼 있는 공종의 `공종코드 → phase_id`.
   *
   * 저장은 공종 행을 지우고 다시 쓰므로, 이걸 안 들고 있으면 매 저장마다 공종의
   * 기본 공정으로 되돌아간다 — 「현장마다 순서를 고칠 수 있다」 는 약속이 조용히 깨진다.
   */
  keptPhase: ReadonlyMap<string, string | null>;
  /**
   * 빈 공종·빈 줄을 털지. **사람이 저장할 때만 true** 다 — 템플릿으로 견적을
   * 만드는 순간에는 끈다. 템플릿의 공종은 「아직 안 채운 뼈대」이지 「안 쓰는
   * 공종」이 아니라, 여기서 털면 기본 템플릿이 아무 일도 하지 않게 된다.
   */
  dropBlank: boolean;
  /** 새 행의 id. 인메모리는 순번, Supabase 는 uuid 를 준다. */
  newId: (kind: "trade" | "item" | "plan") => string;
  at: string;
}

export interface BuiltRows {
  /** `quote` 에 덮을 값. 일정 칸은 `has_schedule` 이 true 일 때만 뜻이 있다. */
  header: Pick<
    Quote,
    | "title"
    | "mgmt_fee_rate"
    | "rounding_adjustment"
    | "subtotal_basic"
    | "subtotal_style"
    | "mgmt_fee"
    | "gross"
    | "total"
    | "vat_mode"
    | "supply_amount"
    | "vat_amount"
    | "conditions"
    | "extra_notice"
    | "expected_start_date"
    | "work_saturday"
    | "work_sunday"
    | "work_holiday"
    | "schedule_holidays"
    | "rough_area_pyeong"
    | "area_m2"
    | "process_standard_version_id"
  >;
  trades: QuoteTrade[];
  items: QuoteItem[];
  /** `has_schedule` 이 false 면 **손대지 않는다는 뜻**이라 빈 배열이다. */
  plans: QuotePhasePlan[];
  has_schedule: boolean;
  dropped_trades: string[];
}

/**
 * 초안 한 벌을 저장할 행으로 바꾼다. **아무것도 쓰지 않는다** — 던지면 저장소가
 * 손대기 전이므로 되돌릴 것이 없다.
 */
export const buildQuoteRows = (input: BuildRowsInput): BuiltRows => {
  const { quote, draft, keptPhase, dropBlank, newId, at } = input;

  const trades = visibleRows(input.trades);
  const phases = visibleRows(input.phases);
  const tradeByCode = new Map(trades.map((t) => [t.code, t]));
  const phaseByCode = new Map(phases.map((p) => [p.code, p]));
  // **합치기 전** 목록으로 만든다. 공종이 든 `phase_id` 는 회사가 덮기 전의
  // 표준 id 일 수 있어, 합친 목록에는 그 행이 없다.
  const phaseCodeById = new Map(input.phases.map((p) => [p.id, p.code]));

  // 빈 공종은 굳히지 않는다. 계산 결과와 자리를 맞춰야 하므로 **계산 전에** 거른다.
  const source = dropBlank
    ? draft.trades
        .filter((trade) => !isBlankTrade(trade))
        // 공종 안의 빈 줄도 같이 털어 낸다. 전부 비면 위에서 이미 걸러졌다.
        .map((trade) => ({
          ...trade,
          items: trade.items.filter((item) => !isBlankItem(item)),
        }))
    : draft.trades;

  const calc = calcQuote({
    mgmt_fee_rate: draft.mgmt_fee_rate,
    rounding_adjustment: draft.rounding_adjustment,
    // 방식을 안 보낸 저장(옛 화면)은 지금 견적의 방식을 이어 쓴다.
    vat_mode: draft.vat_mode ?? quote.vat_mode ?? DEFAULT_VAT_MODE,
    trades: source.map((t) => ({ group: t.group, items: t.items })),
  });

  const tradeRows: QuoteTrade[] = [];
  const itemRows: QuoteItem[] = [];
  // 항목 순서는 견적 전체에서 하나로 센다 — 공종마다 0 부터 세면 조회가 뒤섞인다.
  let order = 0;
  const usedPhaseCodes = new Set<string>();

  source.forEach((tradeInput, tradeIndex) => {
    const tradeCalc = calc.trades[tradeIndex];
    /**
     * 공종 표의 행을 찾아 id·기본 공정을 복사해 둔다. 회사가 이름을 바꿔도 견적이
     * 따라가려면 코드가 아니라 id 로 참조해야 한다.
     *
     * **「이 회사가 쓸 수 있는 코드인가」 를 보는 자리도 여기다.** 스키마는 회사
     * 목록을 모르므로 모양만 보고, 실제 판정은 목록을 들고 있는 이쪽이 한다.
     */
    const tradeDef = tradeByCode.get(tradeInput.trade);
    if (!tradeDef) {
      throw new Error(`알 수 없는 공종입니다 — ${tradeInput.trade}`);
    }

    const tradeRow: QuoteTrade = {
      id: newId("trade"),
      org_id: quote.org_id,
      quote_id: quote.id,
      group: tradeInput.group,
      trade: tradeInput.trade,
      trade_id: tradeDef.id,
      // 이 견적에서 고쳐 둔 것이 있으면 그것이 이긴다.
      phase_id: keptPhase.get(tradeInput.trade) ?? tradeDef.phase_id,
      name: tradeInput.name,
      sort_order: tradeInput.sort_order,
      condition_text: tradeInput.condition_text,
      material_amount: tradeCalc.material_amount,
      labor_amount: tradeCalc.labor_amount,
      amount: tradeCalc.amount,
      created_at: at,
      updated_at: at,
    };
    tradeRows.push(tradeRow);

    /**
     * 이 견적에 실제로 남은 공정. 아래 일정 쓰기가 이 집합으로 줄을 거른다.
     *
     * **코드로 모은다** — 공종은 표준 공정 id 를 들고 있을 수 있는데 일정 줄은
     * 회사가 덮어 쓴 행을 가리킬 수 있다. id 로 맞추면 어긋난다.
     */
    if (tradeRow.phase_id) {
      const code = phaseCodeById.get(tradeRow.phase_id);
      if (code) usedPhaseCodes.add(code);
    }

    const writeItems = (
      inputs: QuoteItemInput[],
      results: CalcItemResult[],
      parentId: string | null
    ): void => {
      inputs.forEach((itemInput, index) => {
        const result = results[index];
        const row: QuoteItem = {
          id: newId("item"),
          org_id: quote.org_id,
          quote_id: quote.id,
          quote_trade_id: tradeRow.id,
          parent_id: parentId,
          sort_order: order++,
          scope: itemInput.scope,
          space_code: itemInput.space_code,
          item: itemInput.item,
          unit: itemInput.unit,
          qty: itemInput.qty,
          material_unit_price: itemInput.material_unit_price,
          labor_unit_price: itemInput.labor_unit_price,
          // SET 부모의 원가는 자식 합이라 단가를 두지 않는다.
          cost_unit_price: itemInput.children.length > 0 ? null : itemInput.cost_unit_price ?? null,
          base_margin_bp: itemInput.children.length > 0 ? null : itemInput.base_margin_bp ?? null,
          material_amount: result.material_amount,
          labor_amount: result.labor_amount,
          amount: result.amount,
          note: itemInput.note,
          // SET 부모의 수량은 자식 합이라 식을 두지 않는다.
          qty_expr: itemInput.children.length > 0 ? null : itemInput.qty_expr || null,
          created_at: at,
          updated_at: at,
        };
        itemRows.push(row);
        if (itemInput.children.length > 0) {
          writeItems(itemInput.children, result.children, row.id);
        }
      });
    };
    writeItems(tradeInput.items, tradeCalc.items, null);
  });

  /**
   * 시공일정. **`schedule` 을 안 주면 손대지 않는다** — 일정 패널을 안 연 화면이
   * 저장할 때 이미 넣어 둔 일정이 조용히 지워지면 안 된다.
   */
  const schedule = draft.schedule;
  const area = areaPairOf(draft.area_m2, draft.area_pyeong);
  const planRows: QuotePhasePlan[] = [];

  if (schedule) {
    /**
     * **시공 순서로 굳힌다.** 화면이 보낸 순서를 그대로 쓰면 「담당자가 며칠을 친
     * 순서」가 고객 공정표의 줄 순서가 된다 — 도배를 먼저 치고 철거를 나중에 치면
     * 공정표 1행이 도배로 나간다. 편집기는 공정 순서로 정렬해 보여 주므로
     * **담당자 화면에서는 멀쩡해 보여 못 알아챈다.**
     */
    const ordered = [...schedule.phases].sort((a, b) => {
      const pa = phaseByCode.get(a.phase_code);
      const pb = phaseByCode.get(b.phase_code);
      return (
        (pa?.sort_order ?? 0) - (pb?.sort_order ?? 0) ||
        a.phase_code.localeCompare(b.phase_code)
      );
    });

    // 한 견적에 같은 공정이 두 줄 서면 공정표에 같은 이름이 두 번 나온다.
    // DB 도 `unique (quote_id, phase_id)` 로 막으므로 여기서도 막아 둔다.
    const seen = new Set<string>();
    for (const p of ordered) {
      if (seen.has(p.phase_code)) {
        throw new Error(`공정이 두 번 들어왔습니다 — ${p.phase_code}`);
      }
      seen.add(p.phase_code);
    }

    ordered.forEach((planInput, index) => {
      // 공종과 같은 규칙 — 「이 회사가 쓸 수 있는 코드인가」 는 목록을 들고 있는
      // 여기서 본다. 스키마는 모양만 봤다.
      const phase = phaseByCode.get(planInput.phase_code);
      if (!phase) {
        throw new Error(`알 수 없는 공정입니다 — ${planInput.phase_code}`);
      }
      // 며칠을 안 채운 공정은 굳히지 않는다. 행으로 남기면 견적서에 빈 줄이 선다.
      if (planInput.duration_days < 1) return;
      /**
       * **그 공정을 하는 공종이 이 견적에 없으면 줄을 만들지 않는다.**
       *
       * 일정 줄은 공종에서 파생된 것이고 화면도 그렇게 그린다. 공종을 빼면
       * 편집기에서는 줄이 사라지는데 저장소에만 남으면, 고객이 받는 공정표에
       * **작업 이름이 빈 줄**이 그대로 선다.
       */
      if (!usedPhaseCodes.has(phase.code)) return;

      planRows.push({
        id: newId("plan"),
        org_id: quote.org_id,
        quote_id: quote.id,
        phase_id: phase.id,
        phase_code: phase.code,
        duration_days: planInput.duration_days,
        start_date: planInput.start_date,
        end_date: planInput.end_date,
        sort_order: index,
        note: planInput.note,
        standard_item_code: planInput.standard_item_code ?? null,
        quantity_m2: planInput.quantity_m2 ?? null,
        crew_size: planInput.crew_size ?? null,
        draft_days: planInput.draft_days ?? null,
        created_at: at,
        updated_at: at,
      });
    });

    /**
     * **여기서 한 번 세어 본다.** 날짜 칸은 사람이 직접 고치므로 `2026` 을 `2062`
     * 로 잘못 칠 수 있는데, 스키마는 형식만 보고 통과시킨다. 그대로 굳으면
     * 상세·인쇄 화면이 렌더 도중 던져 **견적을 열 수조차 없게** 된다.
     *
     * 저장을 거부하면 저장소가 아직 아무것도 안 썼으므로 되돌릴 것이 없다.
     */
    summarizeSchedule(
      planRows
        .filter((p) => p.start_date && p.end_date)
        .map((p) => ({
          phase_id: p.phase_id,
          start_date: p.start_date as string,
          end_date: p.end_date as string,
        })),
      {
        saturday: schedule.work_saturday,
        sunday: schedule.work_sunday,
        holiday: schedule.work_holiday,
      },
      new Set(schedule.holidays ?? [])
    );
  }

  return {
    header: {
      title: draft.title,
      mgmt_fee_rate: draft.mgmt_fee_rate,
      rounding_adjustment: calc.rounding_adjustment,
      subtotal_basic: calc.subtotal_basic,
      subtotal_style: calc.subtotal_style,
      mgmt_fee: calc.mgmt_fee,
      gross: calc.gross,
      total: calc.total,
      vat_mode: calc.vat_mode,
      supply_amount: calc.supply_amount,
      vat_amount: calc.vat_amount,
      // 입력으로 받은 배열을 그대로 들고 있으면 부른 쪽이 push 할 때 저장된 행이 같이 바뀐다.
      conditions: [...draft.conditions],
      extra_notice: draft.extra_notice,
      // 일정을 안 보낸 저장은 지금 값을 그대로 둔다.
      expected_start_date: schedule
        ? schedule.expected_start_date
        : quote.expected_start_date,
      work_saturday: schedule ? schedule.work_saturday : quote.work_saturday,
      work_sunday: schedule ? schedule.work_sunday : quote.work_sunday,
      work_holiday: schedule ? schedule.work_holiday : quote.work_holiday,
      schedule_holidays: schedule
        ? schedule.holidays
          ? [...schedule.holidays]
          : null
        : quote.schedule_holidays,
      // 면적을 안 보낸 저장은 지금 값을 그대로 둔다 · m² 가 원본이고 평은 거기서 계산한다(#173).
      rough_area_pyeong: area.area_pyeong === undefined ? quote.rough_area_pyeong : area.area_pyeong,
      area_m2: area.area_m2 === undefined ? (quote.area_m2 ?? null) : area.area_m2,
      // 판은 한 번 굳으면 안 바뀐다(#384) — 이미 있으면 그것, 없으면 이번 초안이 쓴 판.
      process_standard_version_id: quote.process_standard_version_id ?? schedule?.process_standard_version_id ?? null,
    },
    trades: tradeRows,
    items: itemRows,
    plans: planRows,
    has_schedule: schedule !== undefined && schedule !== null,
    dropped_trades: dropBlank ? droppedTradeNames(draft) : [],
  };
};
